import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';
import fetchClient from 'interfaces/fetchClient';
import Card from 'components/card';
import InputField from 'components/fields/InputField';
import InputTagField from 'components/fields/InputTagField';
import SearchSelector from 'components/fields/SearchSelector';
import { createPricing } from 'interfaces/pricing';
import { updateMyInfo } from 'interfaces/user';
import { useEffect, useState } from 'react';
import env from 'variables/config';
import { IoMdClose } from 'react-icons/io';

const CreatePricingModal = (props: {
    isOpen: any;
    onClose: any;
    products: any;
    onOpen?: () => void;
}) => {
    const { isOpen, onClose, products, onOpen } = props;
    const [user, setUser] = useState<any>();
    const [tags, setTags] = useState<string[]>([]);
    const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

    const handleAddProduct = (product: any) => {
        // Vérifier si le produit n'est pas déjà sélectionné
        if (!selectedProducts.find((p) => p._id === product._id)) {
            setSelectedProducts([...selectedProducts, { ...product, customPrice: product.price }]);
        }
    };

    const handleRemoveProduct = (productId: string) => {
        setSelectedProducts(selectedProducts.filter((p) => p._id !== productId));
    };

    const handlePriceChange = (productId: string, newPrice: number) => {
        setSelectedProducts(
            selectedProducts.map((p) =>
                p._id === productId ? { ...p, customPrice: newPrice } : p
            )
        );
    };

    const resetCreatePricings = () => {
        setTags([]);
        setSelectedProducts([]);
    };

    const handleCreatePricing = async () => {
        // Créer un pricing pour chaque produit sélectionné
        try {
            const promises = selectedProducts.map((product) =>
                createPricing({
                    productId: product._id,
                    price: product.customPrice,
                    tags: tags,
                })
            );

            await Promise.all(promises);
            resetCreatePricings();
            onClose();
        } catch (error) {
            console.error('Erreur lors de la création des tarifs:', error);
        }
    };

    const initUser = () => {
        const token = localStorage.getItem('token');
        const config = {
            headers: { Authorization: `Bearer ${token}` },
        };
        fetchClient
            .get(`${env.apiUrl}/user/me`, config)
            .then((res: any) => {
                const user = res.data;
                setUser(user);
            })
            .catch((err: any) => {
                console.log(err);
            });
    };

    const addNewTag = async (newTag: string) => {
        if (!user) return;
        await updateMyInfo({ tags: [...user.tags, newTag] });
    };

    useEffect(() => {
        initUser();
    }, []);

    useEffect(() => {
        if (isOpen && onOpen) {
            onOpen();
        }
    }, [isOpen]);

    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay className="bg-[#000] !opacity-30" />
            <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[85%] top-[12vh]">
                <ModalBody>
                    <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[750px] flex flex-col !z-[1004]">
                        <h1 className="text-lg font-bold">Tarifs de groupe</h1>
                        <p className="mt-1 text-sm">
                            {' '}
                            Attribuez des tarifs spécifiques à vos groupes de
                            clients
                        </p>
                        <div className="mt-5 grid grid-cols-1 gap-4">
                            <div className="z-50">
                                <InputTagField
                                    label="Tags clients"
                                    placeholder=""
                                    id="tags"
                                    type="text"
                                    extra=""
                                    tags={tags}
                                    allTags={
                                        user
                                            ? user.tags.map((name: string) => ({
                                                  name,
                                              }))
                                            : []
                                    }
                                    onTagCreate={(tag: any) => addNewTag(tag)}
                                    onChange={(newTags: any) => setTags(newTags)}
                                />
                            </div>
                            <div className="z-40">
                                <SearchSelector
                                    displayKey="name"
                                    secondaryKey="ref"
                                    label="Sélectionner des produits*"
                                    options={products.filter(
                                        (p: any) =>
                                            !selectedProducts.find(
                                                (sp: any) => sp._id === p._id
                                            )
                                    )}
                                    onSelect={handleAddProduct}
                                    inputSmall={true}
                                />
                            </div>

                            {/* Liste des produits sélectionnés */}
                            {selectedProducts.length > 0 && (
                                <div className="border rounded-lg p-3 max-h-80 overflow-y-auto">
                                    <h3 className="text-sm font-semibold mb-2 flex items-center justify-between">
                                        <span>Produits sélectionnés ({selectedProducts.length})</span>
                                        {selectedProducts.length > 0 && (
                                            <button
                                                onClick={() => setSelectedProducts([])}
                                                className="text-xs text-red-500 hover:text-red-700"
                                            >
                                                Tout supprimer
                                            </button>
                                        )}
                                    </h3>
                                    <div className="space-y-2">
                                        {selectedProducts.map((product) => (
                                            <div
                                                key={product._id}
                                                className="flex items-center gap-2 p-2 bg-gray-50 rounded"
                                            >
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium">
                                                        {product.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        Ref: {product.ref || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="w-32">
                                                    <InputField
                                                        placeholder="Prix"
                                                        id={`price-${product._id}`}
                                                        type="number"
                                                        value={product.customPrice}
                                                        onChange={(e: any) =>
                                                            handlePriceChange(
                                                                product._id,
                                                                parseFloat(e.target.value) || 0
                                                            )
                                                        }
                                                        inputSmall={true}
                                                        extra="!mb-0"
                                                    />
                                                </div>
                                                <span className="text-sm text-gray-600">€</span>
                                                <button
                                                    onClick={() =>
                                                        handleRemoveProduct(product._id)
                                                    }
                                                    className="text-red-500 hover:text-red-700 p-1"
                                                >
                                                    <IoMdClose size={18} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="mt-8 flex gap-2 mx-auto">
                            <button
                                onClick={() => {
                                    resetCreatePricings();
                                    onClose();
                                }}
                                className="linear h-10 rounded-xl border-2 border-red-400 px-2 text-base font-medium text-red-400 transition duration-200 hover:bg-red-600/5 active:bg-red-700/5"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleCreatePricing}
                                disabled={selectedProducts.length === 0 || tags.length === 0}
                                className={`linear h-10 rounded-xl border-2 px-4 text-base font-medium transition duration-200 ${
                                    selectedProducts.length === 0 || tags.length === 0
                                        ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                        : 'border-toola-500 text-toola-500 hover:bg-toola-600/5 active:bg-toola-700/5'
                                }`}
                            >
                                Créer {selectedProducts.length > 0 && `(${selectedProducts.length} produit${selectedProducts.length > 1 ? 's' : ''})`}
                            </button>
                        </div>
                    </Card>
                </ModalBody>
            </ModalContent>
        </Modal>
    );
};

export default CreatePricingModal;
